import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  User,
  Calendar,
  Clock,
  DollarSign,
  GraduationCap,
  Bell,
  TrendingUp,
  FileText,
  CalendarDays,
  Timer,
  Award,
  Building2,
  Phone,
  MapPin,
  ChevronRight,
  Download,
  Eye,
  Plus,
  Settings,
  Home,
  Briefcase,
  Users,
  Target,
  CheckCircle,
  AlertCircle,
  Info,
} from "lucide-react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";

// Enhanced animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: {
    y: 30,
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  hover: {
    y: -8,
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
};

const headerVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// Professional color scheme
const colorScheme = {
  primary: "from-blue-600 to-blue-700",
  secondary: "from-gray-50 to-gray-100",
  success: "from-green-500 to-green-600",
  warning: "from-amber-500 to-amber-600",
  danger: "from-red-500 to-red-600",
  info: "from-sky-500 to-sky-600",
  purple: "from-purple-500 to-purple-600",
  indigo: "from-indigo-500 to-indigo-600",
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    profile: null,
    leaveBalance: null,
    attendance: null,
    payroll: null,
    training: null,
    competencies: null,
    loading: true,
  });
  const [showAllLeaveTypes, setShowAllLeaveTypes] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [
        profileRes,
        leaveRes,
        attendanceRes,
        payrollRes,
        trainingRes,
        competenciesRes,
      ] = await Promise.all([
        api.get("/auth/me"),
        api.get("/employee-dashboard/me/leave-balance"),
        api.get("/employee-dashboard/me/attendance-summary"),
        api.get("/employee-dashboard/me/latest-payroll"),
        api.get("/employee-dashboard/me/training-progress"),
        api.get("/employee-dashboard/me/competencies"),
      ]);

      setDashboardData({
        profile: profileRes.data.data.user,
        leaveBalance: leaveRes.data.data,
        attendance: attendanceRes.data.data,
        payroll: payrollRes.data.data,
        training: trainingRes.data.data,
        competencies: competenciesRes.data.data,
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardData((prev) => ({ ...prev, loading: false }));
    }
  };

  const getInitials = (prenom, nom) => {
    return `${prenom?.[0] || ""}${nom?.[0] || ""}`.toUpperCase();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(date));
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Administrateur";
      case "rh":
        return "Ressources Humaines";
      case "manager":
        return "Manager";
      case "employe":
        return "Employé";
      default:
        return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 border-red-200";
      case "rh":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "manager":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Navigation handlers
  const handleNavigateToLeaveRequest = () => {
    navigate("/dashboard/leave-management");
  };

  const handleNavigateToAttendance = () => {
    navigate("/dashboard/attendance");
  };

  const handleNavigateToDocuments = () => {
    // For now, redirect to settings since there's no dedicated documents page
    // You can create a documents page later and update this route
    navigate("/dashboard/settings");
  };

  const handleNavigateToSettings = () => {
    navigate("/dashboard/settings");
  };

  const handleNavigateToTraining = () => {
    navigate("/dashboard/training");
  };

  const handleNavigateToCompetencies = () => {
    // For now, redirect to training since competencies might be part of training
    // You can create a dedicated competencies page later
    navigate("/dashboard/training");
  };

  // Professional Loading Skeleton
  if (dashboardData.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="h-8 bg-gray-200 rounded-lg w-1/3 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile skeleton */}
            <div className="lg:col-span-1">
              <Card className="h-80 animate-pulse">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="h-16 w-16 bg-gray-200 rounded-full"></div>
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </CardContent>
              </Card>
            </div>

            {/* Main content skeleton */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                  <Card key={i} className="h-64 animate-pulse">
                    <CardHeader>
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { profile, leaveBalance, attendance, payroll, training, competencies } =
    dashboardData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Professional Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          animate="visible"
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg">
                  <Home className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                    Tableau de bord
                  </h1>
                  <p className="text-gray-600 font-medium">
                    Bonjour {profile?.prenom}, voici un aperçu de votre espace
                    employé
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {new Intl.DateTimeFormat("fr-FR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }).format(new Date())}
                </p>
                <p className="text-xs text-gray-500">Dernière mise à jour</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-gray-200 hover:border-blue-500 hover:bg-blue-50"
                onClick={handleNavigateToSettings}
              >
                <Settings className="h-4 w-4 mr-2" />
                Paramètres
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Main Dashboard Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Profile Section - Left Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <motion.div variants={cardVariants} whileHover="hover">
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-white to-gray-50/50">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600"></div>
                <CardHeader className="pb-4">
                  <div className="flex items-start space-x-4">
                    <Avatar className="h-20 w-20 border-4 border-white shadow-xl">
                      <AvatarImage
                        src={profile?.photo_profil}
                        alt={profile?.prenom}
                      />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xl font-bold">
                        {getInitials(profile?.prenom, profile?.nom)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-xl font-bold text-gray-900 mb-1">
                        {profile?.prenom} {profile?.nom}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-700">
                          {profile?.departement?.nom || "Non assigné"}
                        </span>
                      </CardDescription>
                      <Badge
                        className={`${getRoleColor(
                          profile?.role
                        )} border font-medium`}
                      >
                        {getRoleLabel(profile?.role)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        {profile?.telephone || "Non renseigné"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="text-gray-700">
                        {profile?.lieu_naissance || "Non renseigné"}
                      </span>
                    </div>
                  </div>
                  <Separator />
                  <Link to="/dashboard/settings">
                    <Button
                      variant="outline"
                      className="w-full border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <User className="mr-2 h-4 w-4" />
                      Modifier le profil
                      <ChevronRight className="ml-auto h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Quick Actions */}
            <motion.div variants={cardVariants} whileHover="hover">
              <Card className="border-0 shadow-lg bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="h-5 w-5 text-blue-600" />
                    Actions rapides
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start h-12"
                    onClick={handleNavigateToLeaveRequest}
                  >
                    <CalendarDays className="mr-3 h-4 w-4" />
                    Demande de congé
                    <ChevronRight className="ml-auto h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start h-12"
                    onClick={handleNavigateToAttendance}
                  >
                    <Timer className="mr-3 h-4 w-4" />
                    Pointage
                    <ChevronRight className="ml-auto h-4 w-4" />
                  </Button>
                  {/* <Button
                    variant="outline"
                    className="w-full justify-start h-12"
                    onClick={handleNavigateToDocuments}
                  >
                    <FileText className="mr-3 h-4 w-4" />
                    Documents
                    <ChevronRight className="ml-auto h-4 w-4" />
                  </Button> */}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Main Content - Right Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Top Row - Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Leave Balance Card */}
              <motion.div variants={cardVariants} whileHover="hover">
                <Card className="border-0 shadow-lg bg-white h-full">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Calendar className="h-5 w-5 text-green-600" />
                          Solde de congés
                        </CardTitle>
                        <CardDescription className="text-sm">
                          État de vos congés pour 2025
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-green-200 text-green-700"
                      >
                        2025
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {leaveBalance?.types?.length > 0 ? (
                      <div className="space-y-4">
                        {(showAllLeaveTypes
                          ? leaveBalance.types
                          : leaveBalance.types.slice(0, 2)
                        ).map((type, index) => (
                          <div key={index} className="space-y-2">
                            <div className="flex justify-between items-center text-sm">
                              <span className="font-medium text-gray-900">
                                {type.nom}
                              </span>
                              <span className="text-gray-600">
                                {type.utilises || 0}/{type.autorises} jours
                              </span>
                            </div>
                            <Progress
                              value={
                                ((type.utilises || 0) / type.autorises) * 100
                              }
                              className="h-2"
                            />
                          </div>
                        ))}
                        {leaveBalance.types.length > 2 && (
                          <div className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() =>
                                setShowAllLeaveTypes(!showAllLeaveTypes)
                              }
                            >
                              {showAllLeaveTypes
                                ? "Voir moins"
                                : `+${
                                    leaveBalance.types.length - 2
                                  } autres types`}
                            </Button>
                          </div>
                        )}
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            Demandes en attente
                          </span>
                          <Badge
                            variant={
                              leaveBalance.enAttente > 0
                                ? "secondary"
                                : "outline"
                            }
                            className={
                              leaveBalance.enAttente > 0
                                ? "bg-amber-100 text-amber-800"
                                : ""
                            }
                          >
                            {leaveBalance.enAttente || 0}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">
                          Aucune donnée disponible
                        </p>
                      </div>
                    )}
                    <Button
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                      onClick={handleNavigateToLeaveRequest}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Nouvelle demande
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Attendance Summary Card */}
              <motion.div variants={cardVariants} whileHover="hover">
                <Card className="border-0 shadow-lg bg-white h-full">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Clock className="h-5 w-5 text-blue-600" />
                          Présences
                        </CardTitle>
                        <CardDescription className="text-sm">
                          Résumé du mois en cours
                        </CardDescription>
                      </div>
                      <Badge
                        variant="outline"
                        className="border-blue-200 text-blue-700"
                      >
                        {new Date().toLocaleDateString("fr-FR", {
                          month: "long",
                          year: "numeric",
                        })}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {attendance ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <p className="text-2xl font-bold text-blue-600">
                              {attendance.heures_travaillees || 0}h
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Heures travaillées
                            </p>
                          </div>
                          <div className="text-center p-3 bg-amber-50 rounded-lg">
                            <p className="text-2xl font-bold text-amber-600">
                              {attendance.heures_supplementaires || 0}h
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Heures supplémentaires
                            </p>
                          </div>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            Jours d'absence
                          </span>
                          <Badge
                            variant={
                              attendance.jours_absence > 0
                                ? "destructive"
                                : "outline"
                            }
                            className={
                              attendance.jours_absence > 0
                                ? "bg-red-100 text-red-800"
                                : ""
                            }
                          >
                            {attendance.jours_absence || 0}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">
                          Aucune donnée disponible
                        </p>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-gray-200 hover:border-blue-500 hover:bg-blue-50"
                      onClick={handleNavigateToAttendance}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Voir les détails
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Bottom Row - Payroll, Training, Competencies */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Payroll Overview Card */}
              <motion.div variants={cardVariants} whileHover="hover">
                <Card className="border-0 shadow-lg bg-white h-full">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      Bulletin de paie
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {payroll?.periode_debut
                        ? formatDate(payroll.periode_debut)
                        : "Dernier mois"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {payroll ? (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">
                            {formatCurrency(payroll.salaire_net || 0)}
                          </p>
                          <p className="text-sm text-gray-600 font-medium">
                            Salaire net
                          </p>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Salaire brut</span>
                            <span className="font-medium text-gray-900">
                              {formatCurrency(payroll.salaire_brut || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Primes</span>
                            <span className="font-medium text-green-600">
                              +{formatCurrency(payroll.primes || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cotisations</span>
                            <span className="font-medium text-red-600">
                              -
                              {formatCurrency(
                                payroll.cotisations_sociales || 0
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">
                          Aucun bulletin disponible
                        </p>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-gray-200 hover:border-green-500 hover:bg-green-50"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Télécharger PDF
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Training Progress Card */}
              <motion.div variants={cardVariants} whileHover="hover">
                <Card className="border-0 shadow-lg bg-white h-full">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <GraduationCap className="h-5 w-5 text-purple-600" />
                      Formations
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Votre progression
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {training ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="text-center p-3 bg-purple-50 rounded-lg">
                            <p className="text-xl font-bold text-purple-600">
                              {training.terminees || 0}
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Terminées
                            </p>
                          </div>
                          <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <p className="text-xl font-bold text-blue-600">
                              {training.planifiees || 0}
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Planifiées
                            </p>
                          </div>
                        </div>
                        {training.prochaine && (
                          <>
                            <Separator />
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-gray-900">
                                Prochaine formation
                              </p>
                              <p className="text-sm text-gray-600 line-clamp-2">
                                {training.prochaine.titre}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDate(training.prochaine.date_debut)}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <GraduationCap className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">
                          Aucune formation disponible
                        </p>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-gray-200 hover:border-purple-500 hover:bg-purple-50"
                      onClick={handleNavigateToTraining}
                    >
                      <Award className="mr-2 h-4 w-4" />
                      Voir les formations
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Competencies Card */}
              <motion.div variants={cardVariants} whileHover="hover">
                <Card className="border-0 shadow-lg bg-white h-full">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <TrendingUp className="h-5 w-5 text-indigo-600" />
                      Compétences
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Vos niveaux
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {competencies?.length > 0 ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="p-2 bg-green-50 rounded-lg">
                            <p className="text-lg font-bold text-green-600">
                              {
                                competencies.filter(
                                  (c) => c.niveau === "expert"
                                ).length
                              }
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Expert
                            </p>
                          </div>
                          <div className="p-2 bg-yellow-50 rounded-lg">
                            <p className="text-lg font-bold text-yellow-600">
                              {
                                competencies.filter(
                                  (c) => c.niveau === "intermediaire"
                                ).length
                              }
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Intermédiaire
                            </p>
                          </div>
                          <div className="p-2 bg-red-50 rounded-lg">
                            <p className="text-lg font-bold text-red-600">
                              {
                                competencies.filter(
                                  (c) => c.niveau === "debutant"
                                ).length
                              }
                            </p>
                            <p className="text-xs text-gray-600 font-medium">
                              Débutant
                            </p>
                          </div>
                        </div>
                        <Separator />
                        <div className="space-y-2">
                          {competencies.slice(0, 2).map((comp, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between"
                            >
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {comp.nom_competence}
                              </span>
                              <Badge
                                variant={
                                  comp.niveau === "expert"
                                    ? "default"
                                    : comp.niveau === "intermediaire"
                                    ? "secondary"
                                    : "outline"
                                }
                                className="text-xs"
                              >
                                {comp.niveau}
                              </Badge>
                            </div>
                          ))}
                          {competencies.length > 2 && (
                            <div className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-indigo-600 hover:text-indigo-700"
                              >
                                +{competencies.length - 2} autres
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">
                          Aucune compétence enregistrée
                        </p>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full border-gray-200 hover:border-indigo-500 hover:bg-indigo-50"
                      onClick={handleNavigateToCompetencies}
                    >
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Gérer les compétences
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
